import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { DashboardAuthProvider, useDashboardAuth } from './context/DashboardAuthContext';
import DashboardLogin from './components/Auth/DashboardLogin';
import TableauDeBord from './components/Dashboard/TableauDeBord';
import TempsReel from './components/Dashboard/TempsReel';
import RealTimeSales from './components/Dashboard/RealTimeSales';
import Sidebar from './components/Layout/Sidebar';
import Header from './components/Layout/Header';
import UsersDashboard from './components/Users/UsersDashboard';
import AnalyticsDashboard from './components/Analytics/AnalyticsDashboard';
import GroupsDashboard from './components/Groups/GroupsDashboard';
import Activities from './components/Activities/Activities';
import Sales from './components/Sales/Sales';
import Products from './components/Products/Products';
import Subscriptions from './components/Subscriptions/Subscriptions';
import Settings from './components/Settings/Settings';
import AdminManagement from './components/AdminManagement/AdminManagement';
import DeletionRequests from './components/DeletionRequests/DeletionRequests';
import Registrations from './components/Registrations/Registrations';
import StockDuplication from './components/StockDuplication/StockDuplication';
import ContactMessages from './components/Messages/ContactMessages';
import ChatInbox from './components/Chat/ChatInbox';
import DesktopOverview from './components/Desktop/DesktopOverview';
import DesktopShops from './components/Desktop/DesktopShops';
import DesktopShopDetail from './components/Desktop/DesktopShopDetail';
import useNewItemsBadge from './hooks/useNewItemsBadge';
import usePlatform, { rememberedPlatform } from './hooks/usePlatform';
import './styles/desktop-platform.css';

// "/" is the Web home; an admin who last worked on the Desktop side lands back there instead.
function HomeRoute() {
  return rememberedPlatform() === 'desktop' ? <Navigate to="/desktop" replace /> : <TableauDeBord />;
}

// Protected Route Component with Permission Check
function ProtectedRoute({ children, permission, requireSuperAdmin = false }) {
  const { isAuthenticated, loading, hasPermission, isSuperAdmin } = useDashboardAuth();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Check super admin requirement
  if (requireSuperAdmin && !isSuperAdmin()) {
    return (
      <div className="access-denied-container">
        <h2>🚫 Accès Refusé</h2>
        <p>Seuls le propriétaire et les super admins peuvent accéder à cette page.</p>
      </div>
    );
  }

  if (permission && !hasPermission(permission)) {
    return (
      <div className="access-denied-container">
        <h2>🚫 Accès Refusé</h2>
        <p>Vous n'avez pas la permission d'accéder à cette page.</p>
      </div>
    );
  }

  return children;
}

// Main App Layout
function AppLayout() {
  const { admin, logout } = useDashboardAuth();
  // Each product has its own look (styles/desktop-platform.css), so the side is never in doubt.
  const { platform } = usePlatform();
  // Start closed on mobile (≤768px)
  const [sidebarOpen, setSidebarOpen] = React.useState(() => window.innerWidth > 768);

  const toggleSidebar = () => {
    setSidebarOpen(prev => !prev);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  // New items badge tracking
  const badgeData = useNewItemsBadge();

  // Close sidebar on window resize to mobile
  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className={`app-container platform-${platform}`}>
      <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} badgeData={badgeData} />
      <div className={`main-content ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        <Header 
          user={admin} 
          onLogout={logout} 
          onToggleSidebar={toggleSidebar}
          sidebarOpen={sidebarOpen}
        />
        <div className="content-wrapper">
          <Routes>
            <Route path="/" element={<HomeRoute />} />
            <Route path="/desktop" element={
              <ProtectedRoute permission="desktop.view">
                <DesktopOverview />
              </ProtectedRoute>
            } />
            <Route path="/desktop/shops" element={
              <ProtectedRoute permission="desktop.view">
                <DesktopShops />
              </ProtectedRoute>
            } />
            <Route path="/desktop/shops/:id" element={
              <ProtectedRoute permission="desktop.view">
                <DesktopShopDetail />
              </ProtectedRoute>
            } />
            <Route path="/temps-reel" element={
              <ProtectedRoute permission="realtime.view">
                <TempsReel />
              </ProtectedRoute>
            } />
            <Route path="/realtime" element={
              <ProtectedRoute permission="realtime.view">
                <RealTimeSales />
              </ProtectedRoute>
            } />
            <Route path="/users" element={
              <ProtectedRoute permission="users.view">
                <UsersDashboard />
              </ProtectedRoute>
            } />
            <Route path="/analytics" element={
              <ProtectedRoute permission="analytics.view">
                <AnalyticsDashboard />
              </ProtectedRoute>
            } />
            <Route path="/groups" element={
              <ProtectedRoute permission="groups.view">
                <GroupsDashboard />
              </ProtectedRoute>
            } />
            <Route path="/activities" element={
              <ProtectedRoute permission="activities.view">
                <Activities />
              </ProtectedRoute>
            } />
            <Route path="/sales" element={
              <ProtectedRoute permission="sales.view">
                <Sales />
              </ProtectedRoute>
            } />
            <Route path="/products" element={
              <ProtectedRoute permission="products.view">
                <Products />
              </ProtectedRoute>
            } />
            <Route path="/subscriptions" element={
              <ProtectedRoute permission="sales.view">
                <Subscriptions />
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute permission="settings.view">
                <Settings />
              </ProtectedRoute>
            } />
            <Route path="/admin-management" element={
              <ProtectedRoute permission="admins.view">
                <AdminManagement />
              </ProtectedRoute>
            } />
            <Route path="/stock-duplication" element={
              <ProtectedRoute requireSuperAdmin>
                <StockDuplication />
              </ProtectedRoute>
            } />
            <Route path="/registrations" element={
              <ProtectedRoute permission="approvals.view">
                <Registrations />
              </ProtectedRoute>
            } />
            <Route path="/deletion-requests" element={
              <ProtectedRoute permission="approvals.view">
                <DeletionRequests />
              </ProtectedRoute>
            } />
            <Route path="/contact-messages" element={
              <ProtectedRoute permission="contact.view">
                <ContactMessages />
              </ProtectedRoute>
            } />
            <Route path="/chat" element={
              <ProtectedRoute permission="chat.view">
                <ChatInbox />
              </ProtectedRoute>
            } />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

// App Component
function AppContent() {
  const { isAuthenticated, loading } = useDashboardAuth();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={
        isAuthenticated ? <Navigate to="/" /> : <DashboardLogin />
      } />
      <Route path="/*" element={
        isAuthenticated ? <AppLayout /> : <Navigate to="/login" />
      } />
    </Routes>
  );
}

function App() {
  return (
    <DashboardAuthProvider>
      <Router>
        <AppContent />
      </Router>
    </DashboardAuthProvider>
  );
}

export default App;
